import {
  BarElement,
  CategoryScale,
  Chart,
  Legend,
  LinearScale,
  Tooltip,
} from "chart.js";
import type { ComponentProps } from "react";
import { Bar } from "react-chartjs-2";

import { Sources } from "~/components/charts/sources";
import { abbreviatedNum } from "~/lib/abbreviated-num";
import {
  CHART_BACKGROUND_COLORS,
  CHART_BORDER_COLORS,
  TEXT_GRAY_500,
} from "~/lib/colors";
import { minPercentage } from "~/lib/percentage";

Chart.register([CategoryScale, LinearScale, BarElement, Tooltip, Legend]);

const MIN_PERCENTAGE = 0.15;

type AdjustedToOriginal = Record<string, number>;

type Unit = "seconds" | "$";

const UNITS: Record<Unit, Readonly<{ prefix: boolean }>> = {
  $: { prefix: true },
  seconds: { prefix: false },
};

type Props = Readonly<{
  labels: string[];
  options?: ComponentProps<typeof Bar>["options"];
  sources?: readonly string[];
  values: Record<string, number[]>;
  units: Unit;
}>;

export function HorizontalBar({
  labels,
  options,
  sources = [],
  values,
  units,
}: Props): JSX.Element {
  Chart.defaults.color = TEXT_GRAY_500;

  const isPrefix = UNITS[units].prefix;
  const prefix = isPrefix ? units : "";
  const postfix = isPrefix ? "" : ` ${units}`;
  const { adjusted, adjustedToOriginal } = adjust({ labels, values });
  const datasets = Object.keys(values).map((label) => ({
    backgroundColor: CHART_BACKGROUND_COLORS,
    borderColor: CHART_BORDER_COLORS,
    borderWidth: 1,
    data: adjusted,
    fill: true,
    label,
  }));

  return (
    <>
      <Bar
        data={{ datasets, labels }}
        options={{
          indexAxis: "y",
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: (ctx) => {
                  let label = ctx.dataset.label ?? "";
                  if (label) label += ": ";
                  if (ctx.parsed.x != null) {
                    label += `${prefix}${abbreviatedNum(
                      adjustedToOriginal[
                        getKey(labels[ctx.parsed.y], ctx.parsed.x)
                      ]
                    )}${postfix}`;
                  }
                  return label;
                },
              },
            },
          },
          responsive: true,
          scales: {
            x: {
              grid: { color: "rgba(100, 100, 100, 0.40" },
              ticks: {
                callback: (v) =>
                  typeof v === "number" ? `${prefix}${abbreviatedNum(v)}` : v,
              },
              title: { display: true, text: Object.keys(values)[0] },
            },
          },
          ...options,
        }}
      />
      <Sources sources={sources} />
    </>
  );
}

function adjust({ labels, values }: Pick<Props, "labels" | "values">) {
  const data = values[Object.keys(values)[0]] ?? [];
  const total = data.reduce((acc, v) => acc + v);

  const adjusted = data.map((v) => minPercentage(v, total, MIN_PERCENTAGE));
  return {
    adjusted,
    adjustedToOriginal: data.reduce((acc, v, idx) => {
      acc[getKey(labels[idx], adjusted[idx])] = v;
      return acc;
    }, {} as AdjustedToOriginal),
  };
}

function getKey(label: string, val: number) {
  return `${label}:${val}`;
}
